import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { log, logAttributes } from '@root/app/shared/new-services/logging.service';
import { authService } from '@root/app/shared/new-services/auth.service';
import { isPublicPath } from '@root/app/routing/routeManifest';
import { appSettings } from '@root/app/shared/config/settings';

declare module 'axios' {
    export interface AxiosRequestConfig {
        /**
         * Opt a request out of Sentry reporting. Use it for calls where a failure is an
         * expected outcome rather than a defect — existence probes, optimistic lookups —
         * so they don't drown the real errors.
         */
        skipErrorReporting?: boolean;

        /**
         * Mark a request as a background refresh on a timer rather than something the user
         * asked for. A poll that loses the connection for one tick is a blip: the next tick
         * repairs it and nobody saw anything, so it is logged but not filed as an issue.
         * An HTTP error from a poll is still reported — a 500 is a defect whoever asked.
         */
        backgroundPoll?: boolean;
    }
}

let axiosConfigured = false;

/**
 * Requests still in flight when the page goes away are aborted by the browser and reach the
 * interceptor indistinguishable from a real connection failure. On a screen that polls every
 * few seconds, closing the tab is near-guaranteed to catch one. Nothing failed, so nothing
 * gets reported once this flips.
 */
let pageIsUnloading = false;

const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Endpoint-shaped error. Reporting a string (or the raw AxiosError) makes Sentry group
 * every failed request in the app into a single issue named after this file; a named error
 * whose message is the endpoint gives one issue per broken endpoint instead.
 */
class HttpRequestError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'HttpRequestError';
    }
}

/**
 * Reduce a request URL to the route that failed: drop the origin and the query string, and
 * collapse id-looking path segments. Without this, every distinct id is a distinct issue.
 */
const normalizeRequestPath = (rawUrl?: string): string => {
    if (!rawUrl) {
        return '<unknown>';
    }

    const queryIndex = rawUrl.indexOf('?');
    const withoutQuery = queryIndex === -1 ? rawUrl : rawUrl.slice(0, queryIndex);

    return withoutQuery
        .replace(/^https?:\/\/[^/]+/i, '')
        .split('/')
        .map((segment) => (OBJECT_ID_PATTERN.test(segment) || UUID_PATTERN.test(segment) ? ':id' : segment))
        .join('/');
};

/**
 * The error code the API puts in the body of a failed response (`ErrorMessage.errorCode`).
 */
const apiErrorCode = (error: AxiosError): string | undefined =>
    (error.response?.data as { errorCode?: string } | undefined)?.errorCode;

/**
 * Responses the API returns as a normal, by-design outcome rather than as a defect.
 *
 * `412 NOT_ENOUGH_ACTIVE_EMPLOYEES` is the anonymity threshold: when a segmentation filter
 * narrows the population below the company's minimum, the backend refuses to answer so
 * individual responses can't be inferred from an aggregate. Every stats screen already
 * catches it and renders `hfAnonymousMessage` — see `errorService.isNotEnoughActiveEmployees`
 * and its callers in `enpsBox`, `ENPSFunnelBox`, `ENPSIndividualHeatmap`,
 * `DashboardHappinessIndex` — so filing it as an exception reports working behaviour as a
 * bug, one Sentry group per stats endpoint.
 *
 * Match on status *and* code: 412 alone is not enough, other endpoints use it for real
 * failures (a closed conversation, for one).
 *
 * `400 EMPLOYEE_DOSNT_EXISTS` / `400 HIERARCHY_DOSNT_EXISTS` on a GET is a stale reference:
 * a recognition, survey or announcement still points at a person or area that has since
 * been deleted, and the name lookup for it is answered with "no such entity" (the API maps
 * not-found to 400, not 404). Every lookup site already falls back to `?` /
 * `UNKNOWN_USER_NAME`, so the screen keeps working — HAPPYFORCE-DASHBOARD-11P and -12J were
 * this, filed as defects. Restricted to GET: the same codes on a write mean the action the
 * user asked for did not happen, and that still gets reported.
 */
const EXPECTED_API_OUTCOMES: ReadonlyArray<{ status: number; errorCode: string; method?: string }> = [
    { status: 412, errorCode: 'NOT_ENOUGH_ACTIVE_EMPLOYEES' },
    { status: 400, errorCode: 'EMPLOYEE_DOSNT_EXISTS', method: 'GET' },
    { status: 400, errorCode: 'HIERARCHY_DOSNT_EXISTS', method: 'GET' }
];

const isExpectedApiOutcome = (error: AxiosError): boolean => {
    const status = error.response?.status;
    const errorCode = apiErrorCode(error);
    const method = (error.config?.method ?? 'get').toUpperCase();

    return EXPECTED_API_OUTCOMES.some((outcome) =>
        outcome.status === status
        && outcome.errorCode === errorCode
        && (!outcome.method || outcome.method === method));
};

/**
 * A failure with no `response` never reached the server: DNS, TLS, a dropped connection, a
 * proxy, an extension, a timeout, or the browser tearing the request down. Worth separating
 * from an HTTP error, because none of it can be diagnosed from a status code.
 */
const isTransportFailure = (error: AxiosError): boolean => !error.response;

/**
 * Whether the failure is worth a line in the log stream.
 *
 * Deliberately wider than `shouldReport`: a log is cheap and queryable, so it is the right
 * home for failures that are real but not defects — a poll that missed a tick, a connection
 * that dropped. What gets excluded here is only what nobody could ever act on.
 */
const shouldLog = (error: AxiosError): boolean => {
    if (error.config?.skipErrorReporting) {
        return false;
    }

    // Cancelling is how we abort superseded requests; it isn't a failure. Tested by code
    // rather than `axios.isCancel`, whose type guard narrows AxiosError away to `never`.
    if (error.code === 'ERR_CANCELED') {
        return false;
    }

    // 401 drives the re-login flow below, it is not something to track.
    if (error.response?.status === 401) {
        return false;
    }

    // An offline tab produces these continuously and nothing can be done about them, so
    // only keep them when the browser believes it has a connection. Which also means every
    // transport failure that gets this far was one the browser thought it could make — no
    // point recording `navigator.onLine` alongside it, it is true by construction.
    if (isTransportFailure(error) && typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
    }

    return true;
};

/**
 * Whether the failure is worth an issue — something a person should look at and fix.
 */
const shouldReport = (error: AxiosError): boolean => {
    if (!shouldLog(error)) {
        return false;
    }

    if (isTransportFailure(error)) {
        // The request died with the page, not on its own — see `pageIsUnloading`.
        if (pageIsUnloading) {
            return false;
        }

        // A background refresh that loses the connection for one tick repairs itself on the
        // next one, with no user-visible consequence. Filing it says the dashboard is
        // broken when the honest reading is that one network round-trip didn't land.
        if (error.config?.backgroundPoll) {
            return false;
        }
    }

    return true;
};

/**
 * Example: one wide log per failed request instead of several narrow ones.
 *
 * This is the log-shaped counterpart to `reportRequestFailure`. The exception exists to be
 * grouped and alerted on; this exists to be *queried* — "all 5xx on /surveys/:id since the
 * last deploy" is one filter in the log explorer, across endpoints the issue list would
 * keep in separate buckets. Because the request runs inside a pageload/navigation span, the
 * log is automatically linked to that trace and to the session replay.
 *
 * Level carries meaning: a 5xx is ours to investigate, a 4xx is usually the client asking
 * for something it shouldn't, and a transport failure is a `warn` — it is nearly always the
 * user's network rather than our server, and the attributes below are what tell the two
 * apart when it isn't.
 */
const logRequestFailure = (error: AxiosError): void => {
    const status = error.response?.status;
    const transport = isTransportFailure(error);
    const emit = !transport && (status ?? 0) >= 500 ? log.error : log.warn;

    emit('API request failed', logAttributes({
        'http.request.method': (error.config?.method ?? 'get').toUpperCase(),
        'http.response.status_code': status,
        'http.response.status_text': error.response?.statusText,
        // A transport failure carries no status to reason from, so the diagnosis has to come
        // from the shape of the attempt instead: was the page going away, was anyone waiting
        // on it, and how long did it hang before giving up. Together with `error.type` below
        // — ERR_NETWORK against ECONNABORTED — that is enough to separate "our API was
        // unreachable" from "this tab was closed mid-request". All client-side facts; no
        // URL, header or payload content travels with them.
        'network.transport_failure': transport || undefined,
        'app.page_unloading': transport ? pageIsUnloading : undefined,
        'app.background_poll': error.config?.backgroundPoll || undefined,
        'http.request.timeout_ms': transport ? error.config?.timeout || undefined : undefined,
        // Normalised the same way as the exception's fingerprint, so a log and its issue
        // agree on what "the endpoint" is and ids don't explode attribute cardinality.
        'url.path': normalizeRequestPath(error.config?.url),
        // Which screen the user was on when it broke — the single most useful attribute for
        // reproducing an API failure, and not derivable from the request itself.
        'app.route': typeof window !== 'undefined' ? window.location.pathname : undefined,
        'error.type': error.code
    }));
};

/**
 * An expected outcome is not an error, but it is still worth counting: "how often does a
 * filter land below the anonymity threshold, and on which screens" is a product question,
 * and `info` answers it without putting the event in the error stream where it would be
 * read as a defect.
 */
const logExpectedApiOutcome = (error: AxiosError): void => {
    log.info('API request refused by design', logAttributes({
        'http.request.method': (error.config?.method ?? 'get').toUpperCase(),
        'http.response.status_code': error.response?.status,
        'url.path': normalizeRequestPath(error.config?.url),
        'app.route': typeof window !== 'undefined' ? window.location.pathname : undefined,
        'app.error.code': apiErrorCode(error)
    }));
};

const reportRequestFailure = (error: AxiosError): void => {
    const status = error.response?.status;
    const method = (error.config?.method ?? 'get').toUpperCase();
    const path = normalizeRequestPath(error.config?.url);
    const isNetworkFailure = !error.response;

    const reported = new HttpRequestError(
        isNetworkFailure
            ? `${method} ${path} failed: ${error.code ?? 'network error'}`
            : `${method} ${path} failed with ${status}`
    );

    Sentry.captureException(reported, {
        tags: {
            type: isNetworkFailure ? 'NetworkError' : 'HttpError',
            'http.method': method,
            'http.status': status ? String(status) : 'none',
            'http.path': path
        },
        // Group by endpoint, not by this interceptor's own stack frame.
        fingerprint: [
            'http',
            method,
            path,
            isNetworkFailure ? (error.code ?? 'ERR_NETWORK') : String(status)
        ],
        extra: {
            url: error.config?.url,
            status,
            statusText: error.response?.statusText,
            code: error.code,
            path: typeof window !== 'undefined' ? window.location.pathname : ''
        }
    });
};

const redirectToOnboarding = (): void => {
    if (typeof window !== 'undefined' && !isPublicPath(window.location.pathname)) {
        window.location.assign('/onboarding');
    }
};

export const setupAxios = (): void => {
    if (axiosConfigured) {
        return;
    }
    axiosConfigured = true;

    if (typeof localStorage !== 'undefined') {
        localStorage.setItem('Happyforce-Settings', JSON.stringify(appSettings));
    }

    if (typeof window !== 'undefined') {
        // `pagehide` rather than `beforeunload`: it is the one event that also fires when
        // iOS Safari freezes a tab into the back/forward cache, which aborts in-flight
        // requests exactly the same way. A restored page has to clear the flag again,
        // otherwise a bfcache round-trip would silence reporting for the rest of the session.
        window.addEventListener('pagehide', () => { pageIsUnloading = true; });
        window.addEventListener('pageshow', () => { pageIsUnloading = false; });
    }

    axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        const headers = config.headers ?? {};

        if (config.params && (config.params as Record<string, unknown>).companyId) {
            headers.companyId = (config.params as Record<string, unknown>).companyId;
        }

        const session = authService.sessionId();
        if (session) {
            headers.SessionId = session;
        }

        config.headers = headers;
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        (error: AxiosError) => {
            // This interceptor is the single place HTTP failures are reported from.
            // Callers that also catch the error must not capture it again — see
            // `errorService.reportError`.
            //
            // A response the API refuses by design is neither a defect to file nor noise to
            // drop: it is logged at `info` and stays out of the error stream entirely.
            //
            // Everything else goes through two gates of deliberately different widths. A
            // failure that is real but not a defect — a poll that missed a tick, a request
            // the browser killed on navigation — still earns a log line, where it can be
            // counted and correlated, without claiming a person needs to go fix something.
            if (isExpectedApiOutcome(error)) {
                logExpectedApiOutcome(error);
            } else {
                if (shouldLog(error)) {
                    logRequestFailure(error);
                }

                if (shouldReport(error)) {
                    reportRequestFailure(error);
                }
            }

            if (error.response?.status === 401 && authService.isLoggedIn()) {
                authService.invalidate();
                redirectToOnboarding();
            }

            return Promise.reject(error);
        }
    );
};
